import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { serverTypeLabel, serverTypeColor } from '@/utils/server-type';
import { typedCountConfirms, apiErrorText } from '@/utils/channel-actions';
import { skipReasonKey } from '@/utils/leg-actions';
import { getErrorMessage, getErrorCode } from '@/services/api';

export interface ConfirmServerRow {
  serverId: number;
  name: string;
  type: string;
  running: number;
  stopped: number;
  /** Optional detail lines (e.g. the stream names that will be touched). */
  items?: string[];
  /** Richer lines (scoped leg actions): what will be done to each stream; shown instead of `items`. */
  entries?: Array<{ text: string; detail?: string; danger?: boolean; note?: string }>;
}

export interface ConfirmActionConfig {
  title: string;
  intro: string;
  servers: ConfirmServerRow[];
  /** Channel names involved (page-level actions). */
  names?: string[];
  confirmLabel: string;
  tone: 'danger' | 'primary';
  /** When set, the confirm button stays disabled until this number is typed. */
  typedCount?: number;
  /** Items the action will NOT touch, with the reason (scoped leg actions: built from the server's plan). */
  skipped?: Array<{ server: string; name: string; reason: string }>;
  run: () => Promise<void>;
}

/**
 * Preview + explicit confirmation before any bulk, destructive or restarting action: which servers, how many
 * streams running / stopped. Nothing runs until Confirm is clicked.
 */
/**
 * `onClose(closing)` receives the config being closed: the parent must clear its state only if that config is still the
 * current one (an action may have opened a follow-up confirmation, which must stay): see `closeAfterRun`.
 */
export default function ConfirmActionModal({ config, onClose }: { config: ConfirmActionConfig | null; onClose: (closing: ConfirmActionConfig) => void }) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  // Set synchronously: a double click / Enter twice can never run the action twice.
  const running = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => { setTyped(''); setBusy(false); running.current = false; }, [config]);

  // Focus goes back to the control that opened the dialog when it closes.
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (config) {
      if (!opener.current) opener.current = document.activeElement as HTMLElement | null;
      return;
    }
    const el = opener.current;
    opener.current = null;
    if (el && document.contains(el)) el.focus();
  }, [config]);

  // Initial focus (the count field, else Cancel: never the destructive button), Escape closes, Tab stays inside.
  useEffect(() => {
    if (!config) return;
    (inputRef.current ?? cancelRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !running.current) { e.preventDefault(); onCloseRef.current(config); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [config]);

  if (!config) return null;
  const needsTyping = config.typedCount !== undefined;
  const canConfirm = !busy && (!needsTyping || typedCountConfirms(typed, config.typedCount!));

  const confirm = async () => {
    if (!canConfirm || running.current) return;
    running.current = true;
    setBusy(true);
    const started = config;
    try {
      await started.run();
    } catch (error) {
      toast.error(apiErrorText({ code: getErrorCode(error), message: getErrorMessage(error) }, t) || t('common.error'));
    } finally {
      running.current = false;
      setBusy(false);
      // The parent closes it only if the action did not open a follow-up confirmation meanwhile.
      onCloseRef.current(started);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => { if (!running.current) onClose(config); }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="confirm-action-title" className="card w-full max-w-lg animate-fade-in flex flex-col max-h-[calc(100vh-2rem)]" onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-action-title" className="text-lg font-semibold text-dark-100">{config.title}</h2>
        <p className="text-sm text-dark-400 mt-1">{config.intro}</p>

        <div className="mt-4 space-y-2 overflow-y-auto">
          {config.servers.length === 0 ? (
            !config.names?.length && <p className="text-sm text-dark-500">{t('linear.confirm.noServers')}</p>
          ) : config.servers.map((s) => (
            <div key={s.serverId} className="rounded-lg border border-dark-700 px-3 py-2">
              <div className="flex items-center gap-2 text-sm">
                <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${serverTypeColor(s.type)}`}>{serverTypeLabel(s.type)}</span>
                <span className="font-medium text-dark-100">{s.name}</span>
                <span className="ml-auto text-xs text-dark-400">
                  {s.running > 0 && <span className="text-success-600">{t('linear.confirm.runningCount', { count: s.running })}</span>}
                  {s.running > 0 && s.stopped > 0 && ', '}
                  {s.stopped > 0 && <span>{t('linear.confirm.stoppedCount', { count: s.stopped })}</span>}
                </span>
              </div>
              {s.entries && s.entries.length > 0 ? (
                <ul className="mt-1 space-y-0.5">
                  {s.entries.map((e, i) => (
                    <li key={`${e.text}-${i}`} className={`text-xs break-words ${e.danger ? 'text-error-600' : 'text-dark-300'}`}>
                      <span className="font-medium">{e.detail}</span> <span className="font-mono">{e.text}</span>
                      {e.note && <span className="block text-warning-600">{e.note}</span>}
                    </li>
                  ))}
                </ul>
              ) : s.items && s.items.length > 0 && (
                <p className="text-xs text-dark-500 mt-1 font-mono break-words">{s.items.join(', ')}</p>
              )}
            </div>
          ))}
          {config.skipped && config.skipped.length > 0 && (
            <div className="rounded-lg border border-dark-700 px-3 py-2">
              <p className="text-xs font-medium text-dark-300">{t('linear.legs.previewSkipped')}</p>
              <ul className="mt-1 space-y-0.5">
                {config.skipped.map((x, i) => (
                  <li key={`${x.server}-${x.name}-${i}`} className="text-xs text-dark-500 break-words">
                    {x.server}: <span className="font-mono">{x.name}</span> ({t(skipReasonKey(x.reason))})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {config.names && config.names.length > 0 && (
            <p className="text-xs text-dark-500 break-words">{t('linear.confirm.channels')}: {config.names.join(', ')}</p>
          )}
        </div>

        {needsTyping && (
          <label className="block mt-4 text-sm text-dark-300">
            {t('linear.confirm.typeCount', { count: config.typedCount })}
            <input
              ref={inputRef}
              inputMode="numeric"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
              className="input mt-1 w-32"
            />
          </label>
        )}

        <div className="flex justify-end gap-3 mt-5">
          <button ref={cancelRef} type="button" onClick={() => onClose(config)} disabled={busy} className="btn-secondary">{t('common.cancel')}</button>
          <button
            type="button"
            onClick={confirm}
            disabled={!canConfirm}
            className={config.tone === 'danger' ? 'btn-primary bg-error-600 hover:bg-error-500 disabled:opacity-50' : 'btn-primary disabled:opacity-50'}
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {config.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
